import { useAuth } from './useAuth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { AISettings, AIProvider } from '../types';
import { AI_PROVIDERS } from '../utils/constants';

const MANAGE_AI_KEYS_FUNCTION = 'manage-ai-keys';

export const useAISettings = () => {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  // Fetch all AI settings
  const aiSettingsQuery = useQuery<AISettings[]>({
    queryKey: ['ai_settings'],
    queryFn: async ({ signal }) => {
      if (signal?.aborted) {
        throw new DOMException('Query was aborted', 'AbortError');
      }

      try {
        const { data, error } = await supabase
          .from('ai_settings')
          .select('*')
          .order('provider', { ascending: true });

        if (signal?.aborted) {
          throw new DOMException('Query was aborted', 'AbortError');
        }

        if (error) {
          if (error.code === '42501' || error.message.includes('RLS') || error.message.includes('policy') || error.message.includes('permission')) {
            throw new Error(`RLS Policy Error: ${error.message}. Ensure you are authenticated as an admin with @thesimpleintel.com email.`);
          }
          throw error;
        }

        return (data || []) as AISettings[];
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw err;
        }
        throw err;
      }
    },
    enabled: !!isAdmin,
    staleTime: 30000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Get settings for a specific provider
  const getProviderSettings = (provider: AIProvider): AISettings | null => {
    const settings = aiSettingsQuery.data || [];
    return settings.find(s => s.provider === provider) || null;
  };

  // Get default settings for a provider (if not configured)
  const getDefaultSettings = (provider: AIProvider): Partial<AISettings> => {
    const providerInfo = AI_PROVIDERS[provider];
    return {
      provider,
      enabled: false,
      default_model: providerInfo.defaultModel,
      config: providerInfo.defaultConfig,
      rate_limits: providerInfo.defaultRateLimits,
    };
  };

  // Update AI settings mutation (API keys go via Edge Function; other fields via direct upsert)
  const updateSettingsMutation = useMutation({
    mutationFn: async ({
      provider,
      updates,
    }: {
      provider: AIProvider;
      updates: Partial<Omit<AISettings, 'id' | 'created_at' | 'updated_at'>>;
    }) => {
      const existing = getProviderSettings(provider);
      const defaultSettings = getDefaultSettings(provider);

      const mergedConfig = {
        ...defaultSettings.config,
        ...(existing?.config || {}),
        ...(updates.config || {}),
      };

      const mergedRateLimits = {
        ...defaultSettings.rate_limits,
        ...(existing?.rate_limits || {}),
        ...(updates.rate_limits || {}),
      };

      const hasNewApiKey = updates.api_key_encrypted != null && updates.api_key_encrypted.trim() !== '';

      // If a new API key is provided, store it securely via Edge Function (never send key in client upsert)
      if (hasNewApiKey) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('Session expired. Please sign out and sign in again.');
        }
        const { data: fnData, error: fnError } = await supabase.functions.invoke(MANAGE_AI_KEYS_FUNCTION, {
          method: 'POST',
          body: { provider, apiKey: updates.api_key_encrypted!.trim() },
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        const bodyError =
          typeof fnData?.error === 'string'
            ? fnData.error
            : typeof (fnData as { message?: string } | null)?.message === 'string'
              ? (fnData as { message: string }).message
              : null;
        if (fnError || bodyError) {
          const detail = bodyError || fnError?.message || 'Failed to store API key securely.';
          if (/not found|404|Failed to send/i.test(detail)) {
            throw new Error(
              'manage-ai-keys Edge Function is unavailable. Deploy it or check Supabase Edge Functions.'
            );
          }
          throw new Error(detail);
        }
      }

      const settingsToUpsert: Partial<AISettings> = {
        provider,
        enabled: updates.enabled ?? existing?.enabled ?? false,
        default_model: updates.default_model ?? existing?.default_model ?? defaultSettings.default_model ?? null,
        config: mergedConfig,
        rate_limits: mergedRateLimits,
      };
      // Do not include api_key_encrypted in client upsert; key was already stored via Edge Function
      if (!hasNewApiKey && existing?.api_key_encrypted != null) {
        settingsToUpsert.api_key_encrypted = existing.api_key_encrypted;
      }

      const { error } = await supabase
        .from('ai_settings')
        .upsert(settingsToUpsert, {
          onConflict: 'provider',
        });

      if (error) {
        if (error.code === '42501' || error.message.includes('RLS') || error.message.includes('policy')) {
          throw new Error(`RLS Policy Error: ${error.message}. Ensure you are authenticated as an admin.`);
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai_settings'] });
    },
  });

  // Delete AI settings mutation
  const deleteSettingsMutation = useMutation({
    mutationFn: async (provider: AIProvider) => {
      const { error } = await supabase
        .from('ai_settings')
        .delete()
        .eq('provider', provider);

      if (error) {
        if (error.code === '42501' || error.message.includes('RLS') || error.message.includes('policy')) {
          throw new Error(`RLS Policy Error: ${error.message}. Ensure you are authenticated as an admin.`);
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai_settings'] });
    },
  });

  return {
    aiSettings: aiSettingsQuery.data || [],
    aiSettingsLoading: aiSettingsQuery.isLoading,
    aiSettingsError: aiSettingsQuery.error instanceof DOMException && aiSettingsQuery.error.name === 'AbortError' ? null : aiSettingsQuery.error,
    refetchAISettings: aiSettingsQuery.refetch,
    getProviderSettings,
    getDefaultSettings,
    updateSettings: updateSettingsMutation.mutate,
    updateSettingsAsync: updateSettingsMutation.mutateAsync,
    updateSettingsLoading: updateSettingsMutation.isPending,
    deleteSettings: deleteSettingsMutation.mutate,
    deleteSettingsLoading: deleteSettingsMutation.isPending,
  };
};
